import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { listOrganizations } from '@/features/organizations/organizationsThunks';
import {
  selectOrganizations,
  selectOrganizationsError,
  selectOrganizationsLoading,
} from '@/features/organizations/organizationsSlice';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ErrorNotice } from '@/components/ui/error-notice';

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function formatDate(timestamp?: number) {
  if (!timestamp) return 'N/A';
  const ms = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadge(status: string) {
  if (status === 'active') {
    return (
      <Badge variant="default" className="bg-green-600">
        Active
      </Badge>
    );
  }

  if (status === 'suspended') {
    return <Badge variant="secondary">Suspended</Badge>;
  }

  return <Badge variant="outline">Inactive</Badge>;
}

export default function AdminOrganizationsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const organizations = useAppSelector(selectOrganizations);
  const isLoading = useAppSelector(selectOrganizationsLoading);
  const error = useAppSelector(selectOrganizationsError);
  const hasListedAllOrganizations = useAppSelector((state) => state.organizations.hasListedAllOrganizations);

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (hasListedAllOrganizations) return;

    dispatch(listOrganizations())
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load organizations');
      });
  }, [dispatch, hasListedAllOrganizations]);

  const filteredOrganizations = useMemo(() => {
    const q = normalize(query);
    if (!q) return organizations;

    return organizations.filter((org) => {
      return (
        normalize(org.name ?? '').includes(q) ||
        normalize(org.slug ?? '').includes(q) ||
        normalize(org.id ?? '').includes(q) ||
        normalize(org.ownerID ?? '').includes(q)
      );
    });
  }, [organizations, query]);

  const handleRefresh = async () => {
    if (isLoading) return;

    try {
      await dispatch(listOrganizations()).unwrap();
      toast.success('Organizations refreshed');
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to refresh organizations');
    }
  };

  if (isLoading && organizations.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Admin · Organizations</h1>
          <p className="text-muted-foreground text-sm">Browse and manage organizations in the system.</p>
          <ErrorNotice className="mt-2" message={error} title="Failed to Load Organizations" />
        </div>

        <Button type="button" variant="outline" onClick={handleRefresh} disabled={isLoading}>
          {isLoading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organizations ({organizations.length})
          </CardTitle>
          <CardDescription>Click an organization row to open details.</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, slug, id, owner…"
          />

          {filteredOrganizations.length === 0 ? (
            <div className="border-muted-foreground/25 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
              <Building2 className="text-muted-foreground mb-2 h-8 w-8" />
              <h3 className="font-medium">No organizations found</h3>
              <p className="text-muted-foreground text-sm">
                {query ? 'Try a different search term.' : 'There are no organizations in the system yet.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Capabilities</TableHead>
                  <TableHead>Owner ID</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrganizations.map((org) => (
                  <TableRow
                    key={org.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/admin/organizations/${org.id}`, {
                        state: { fromOrganizationsList: true },
                      })
                    }
                  >
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>{org.slug}</TableCell>
                    <TableCell>{getStatusBadge(org.status)}</TableCell>
                    <TableCell>{org.capabilities?.length ?? 0}</TableCell>
                    <TableCell className="font-mono text-xs">{org.ownerID || 'N/A'}</TableCell>
                    <TableCell>{formatDate(org.updatedAt || org.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
